<template>
  <div class="subPanel">
    <button
      :class="{
        disabledFlow: disabled,
      }"
      class="expandPanel"
      @click="collapse"
    >
      <p>{{ parseTranslation(FLOW) }}</p>
      <span v-if="!disabled" :class="{ active: collapseFlow }">
        <Arrow />
      </span>
    </button>

    <div class="collapsePanel" v-if="collapseFlow">
      <div v-if="!disabled">
        <div class="next-question">
          <label :for="`nextQuestion-${question_index}`">
            {{ translations[language][NEXT_QUESTION] }}
          </label>

          <select
            class="select next quest"
            :id="`nextQuestion-${question_index}`"
            v-model="logicDefault"
            :disabled="locked"
          >
            <option v-for="question in questionsToGoTo" :key="question" :value="question">
              {{ question }}
            </option>

            <option v-if="code !== questions[questions.length - 1].code" value="next">
              {{ parseTranslation(NEXT) }}
            </option>

            <option v-if="code !== questions[questions.length - 1].code" value="screened">
              {{ parseTranslation(SCREENED) }}
            </option>

            <option
              v-if="code === questions[questions.length - 1].code"
              :selected="code === questions[questions.length - 1].code"
              value="finish"
            >
              {{ parseTranslation(FINISH) }}
            </option>
          </select>
        </div>

        <div class="condition-wrapper" v-if="conditions.length">
          <div
            v-for="[conditionIndex, condition] in conditions.entries()"
            :key="conditionIndex"
          >
            <div v-if="condition.conditionItens.length">
              <div
                v-for="[itemIndex, conditionItem] in condition.conditionItens.entries()"
                :key="itemIndex"
              >
                <div class="condition">
                  <p v-if="itemIndex === 0">
                    {{ parseTranslation(IF_YOU_ANSWER) }}
                  </p>
                  <select
                    v-else
                    class="select and-or"
                    :id="`condition-operator-${itemIndex}`"
                    v-model="conditionItem.operator"
                    @change="() => updateLogic(getLogicFormatted())"
                    :disabled="locked"
                  >
                    <option value="AND">{{ parseTranslation(AND) }}</option>
                    <option value="OR">{{ parseTranslation(OR) }}</option>
                  </select>

                  <select
                    class="select"
                    :id="`condition-question-${itemIndex}`"
                    v-model="conditionItem.question"
                    @change="
                      () => {
                        changeQuestion(conditionItem)
                        updateLogic(getLogicFormatted())
                      }
                    "
                    :disabled="locked"
                  >
                    <option
                      v-for="qst in validQuestionOptions"
                      :key="qst.qid"
                      :value="qst"
                    >
                      {{ qst.code.toString().replace(/[\W_]/g, '') }}
                    </option>
                  </select>

                  <select
                    v-if="conditionItem.question.subQuestions.length"
                    class="select ans"
                    :id="`condition-sub-${itemIndex}`"
                    v-model="conditionItem.sub"
                    @change="() => updateLogic(getLogicFormatted())"
                    :disabled="locked"
                  >
                    <option
                      v-for="(subQuestion, subQuestionIndex) in conditionItem.question
                        .subQuestions"
                      :key="subQuestionIndex"
                      :value="subQuestion"
                    >
                      {{ subQuestion.code.toString().replace(/[\W_]/g, '') }}
                    </option>
                  </select>
                  <select
                    v-if="conditionItem.question.answers.length"
                    class="select ans"
                    :id="`condition-answer-${itemIndex}`"
                    v-model="conditionItem.answer"
                    @change="() => updateLogic(getLogicFormatted())"
                    :disabled="locked"
                  >
                    <option
                      v-for="(answer, answerIndex) in conditionItem.question.answers"
                      :key="answerIndex"
                      :value="answer"
                    >
                      {{ answer.code.toString().replace(/[\W_]/g, '') }}
                    </option>
                  </select>

                  <button
                    class="add-condition-btn add-condition"
                    @click="addConditionItem(conditionIndex)"
                    v-if="itemIndex === condition.conditionItens.length - 1 && !locked"
                  >
                    <span class="plus-sign"> + </span>
                  </button>

                  <div
                    class="to"
                    v-if="itemIndex === condition.conditionItens.length - 1"
                  >
                    <p>{{ parseTranslation(GO_TO) }}</p>

                    <select
                      class="select quest goto"
                      :id="`to-question-${conditionIndex}`"
                      v-model="conditions[conditionIndex].to"
                      @change="() => updateLogic(getLogicFormatted())"
                      :disabled="locked"
                    >
                      <option
                        v-for="question in questionsToGoTo"
                        :key="question"
                        :value="question"
                      >
                        {{ question }}
                      </option>

                      <option value="screened">
                        {{ translations[language][SCREENED] }}
                      </option>
                    </select>
                  </div>

                  <button
                    v-if="!locked"
                    @click="removeConditionItem(conditionIndex, itemIndex)"
                    class="delete-condition"
                  >
                    <Close />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="no-condition">
          <p>{{ parseTranslation(NO_CONDITION) }}</p>
        </div>

        <button
          class="add-condition-btn"
          v-if="validQuestionOptions.length && !locked"
          @click="addCondition"
        >
          <span class="plus-sign">+</span>

          <span>{{ parseTranslation(ADD_CONDITION) }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import Arrow from '@/components/imgs/ArrowExpandGroup'
import Close from '@/components/imgs/Close'

import { questionsConfig } from '@/utils/questions'
import { getFirstItem, getLastIndex, getNextIndex, getLastItem } from '@/utils/array'

import * as translationFlow from './Flow.translations'

const { default: translations, ...translationKeys } = translationFlow

export default {
  name: 'QuestionFlow',

  components: { Arrow, Close },

  props: {
    question_index: { type: Number, required: true },
    groupIndex: { type: Number, required: true },
    isMessage: { type: Boolean, required: true },
    expandAll: { required: true },
    deleteConditions: { required: true },
    locked: {
      default: false,
    },
    disabled: { required: true },
  },

  data() {
    return {
      translations,
      ...translationKeys,
      conditions: [],
      collapseFlow: false,
    }
  },

  computed: {
    ...mapState(['questionGroups']),

    questions() {
      return this.questionGroups[this.groupIndex].questions
    },

    allQuestionsCodes() {
      return this.questions.map(question => question.code)
    },

    questionsToGoTo() {
      return this.questions
        .filter((_, index) => index > this.question_index)
        .map(question => question.code)
    },

    validQuestionOptions() {
      return this.questions.filter((question, index) => {
        const { type, answers, subQuestions } = question

        const isBefore = index <= this.question_index
        const { canBeReferencedInFlow, hasAnswer, hasSubQuestion } = questionsConfig[type]

        if (!isBefore || !canBeReferencedInFlow) return false

        const answersOk = hasAnswer ? !!answers.length : true
        const subQuestionsOk = hasSubQuestion ? !!subQuestions.length : true

        return answersOk && subQuestionsOk
      })
    },

    question() {
      return this.questions[this.question_index]
    },

    logic() {
      return this.question.logic
    },

    logicDefault: {
      get() {
        return this.question.logic.default
      },

      set(value) {
        this.updateLogic(this.getLogicFormatted(value))
      },
    },

    code() {
      return this.question.code
    },

    questionGroup() {
      return this.questionGroups[this.groupIndex]
    },
  },

  watch: {
    allQuestionsCodes() {
      this.updateLogic(this.getLogicFormatted())
    },

    questions() {
      this.validateNextQuestion()

      this.startConditions(this.logic)
    },

    deleteConditions() {
      this.startConditions(this.logic)
    },

    expandAll() {
      this.collapseFlow = !!this.expandAll
    },
  },

  methods: {
    ...mapActions(['setUpdateLog', 'changeSpecificQuestionProperty']),

    validateNextQuestion() {
      const defaultIsFinish = this.questions.find((question, index) => {
        return (
          question.logic.default === 'finish' && index !== getLastIndex(this.questions)
        )
      })

      if (defaultIsFinish) this.logicDefault = 'next'
    },

    getLogicFormatted(defaultValue) {
      const conditions = this.conditions.map(condition => {
        let string = ''

        condition.conditionItens.forEach((item, index) => {
          const { answer, sub, operator, question } = item

          if (!answer && !sub) return

          if (index > 0 && operator) {
            string = `${string} ${operator} `
          }

          string += question.code

          if (sub) {
            const subCode = sub.code.toString().replace(/[\W_]/g, '')
            string = `${string}.${subCode}`
          }

          if (answer) {
            const subCode = answer.code.toString().replace(/[\W_]/g, '')
            string = `${string}.${subCode}`
          }
        })

        return {
          goTo: condition.to?.code || condition.to,
          logic: string,
        }
      })

      const logicDefault = this.logic.default?.code || this.logic.default

      return {
        default: defaultValue || logicDefault,
        conditions,
      }
    },

    validateLogicIsEqual(newLogic) {
      const existingLogic = this.question.logic
      const sameDefault = existingLogic?.default === newLogic.default
      const sameLength = existingLogic?.conditions.length === newLogic.conditions.length

      if (!sameDefault || !sameLength) return false

      const sameConditions = newLogic.conditions.every(({ goTo, logic }, index) => {
        const existingConditions = existingLogic.conditions
        const sameGoTo = goTo === existingConditions[index].goTo
        const sameLogic = logic === existingConditions[index].logic
        return sameGoTo && sameLogic
      })

      return sameConditions
    },

    updateLogic(value) {
      const sameLogic = this.validateLogicIsEqual(value)

      if (sameLogic) return

      this.changeSpecificQuestionProperty({
        questionGroupIndex: this.groupIndex,
        questionIndex: this.question_index,
        property: 'logic',
        value,
      })
    },

    changeQuestion(conditionItem) {
      const {
        question: { answers, subQuestions },
      } = conditionItem

      conditionItem.answer = getFirstItem(answers)

      if (subQuestions.length) conditionItem.sub = getFirstItem(subQuestions)
    },

    collapse() {
      if (this.disabled) return

      this.collapseFlow = !this.collapseFlow
    },

    addConditionItem(index) {
      const questionReference = getLastItem(this.validQuestionOptions)

      const newItem = {
        operator: this.conditions[index].conditionItens.length ? 'AND' : '',
        question: questionReference,
        sub: getFirstItem(questionReference.subQuestions) || '',
        answer: getFirstItem(questionReference.answers) || '',
      }

      this.conditions[index].conditionItens.push(newItem)

      this.updateLogic(this.getLogicFormatted())
    },

    removeConditionItem(conditionIndex, conditionItemIndex) {
      this.conditions[conditionIndex].conditionItens.splice(conditionItemIndex, 1)

      if (!this.conditions[conditionIndex].conditionItens.length) {
        this.conditions.splice(conditionIndex, 1)
      }

      this.updateLogic(this.getLogicFormatted())
    },

    addCondition() {
      const questionIsLast = getLastIndex(this.questions) === this.question_index

      const condition = {
        to: questionIsLast
          ? 'screened'
          : this.questions[getNextIndex(this.question_index)],
        conditionItens: [],
      }
      this.conditions.push(condition)
      // logic update happens inside this call
      this.addConditionItem(getLastIndex(this.conditions))
    },

    removeUndefinedProperties(object) {
      const curated = Object.entries(object).reduce(
        (acc, [key, value]) => (value === undefined ? acc : { ...acc, [key]: value }),
        {},
      )
      return curated
    },

    parseLogicString(logicString) {
      const operators = []
      const questions = []
      const logicParts = logicString.split(' ')

      logicParts.forEach(item => {
        if (!item.length) return

        if (item === 'OR' || item === 'AND') return operators.push(item)

        const [code, alternative1, alternative2] = item.split('.')

        questions.push(
          this.removeUndefinedProperties({
            question: code,
            answer: alternative2 || alternative1,
            sub: alternative2 ? alternative1 : undefined,
          }),
        )
      })

      return {
        questions,
        operators,
      }
    },

    getConditionItems(questions, operators) {
      let operatorIndex = 0
      const conditionItems = []
      let invalidExpression = false

      questions.forEach((qst, index) => {
        const question = this.validQuestionOptions.find(
          ({ code }) => code === qst.question,
        )

        if (!question) return (invalidExpression = true)

        const { answers, subQuestions, type } = question

        const answer = answers.find(({ code }) => `${code}` === `${qst.answer}`)
        const subQuestion = subQuestions.find(({ code }) => `${code}` === `${qst.sub}`)

        const isFirstQuestion = index === 0

        const operator = isFirstQuestion ? undefined : operators[operatorIndex]

        const conditionObject = {
          question,
          answer,
          sub: subQuestion,
          operator,
        }

        if (operator) operatorIndex += 1

        const { hasSubQuestion } = questionsConfig[type]

        if (!answer || (hasSubQuestion && !subQuestion)) invalidExpression = true

        if (this.question.code === 'A18') console.log({ answer, subQuestion })

        conditionItems.push(this.removeUndefinedProperties(conditionObject))
      })

      if (invalidExpression) return null

      return conditionItems
    },

    startConditions(questionLogic) {
      const conditions = questionLogic.conditions.map(condition => {
        const { operators, questions } = this.parseLogicString(condition.logic)

        const conditionItems = this.getConditionItems(questions, operators)

        const isSpecialGoTo = ['next', 'finish', 'screened'].includes(condition.goTo)

        const questionGoTo = isSpecialGoTo
          ? condition.goTo
          : this.questions.find(({ code }) => code === condition.goTo)

        if (!questionGoTo || !conditionItems) return null

        return {
          to: questionGoTo?.code || questionGoTo,
          conditionItens: conditionItems,
        }
      })

      this.conditions = conditions.filter(Boolean)

      this.updateLogic(this.getLogicFormatted())
    },
  },

  created() {
    this.startConditions(this.logic)
  },
}
</script>
<style lang="scss" scoped>
.expandPanel {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border: none;
  width: 100%;
  cursor: pointer;
  font-size: inherit;
  background: none;

  &:focus {
    outline: none;
  }

  p {
    @include tipography(16);
    font-weight: bold;
    white-space: nowrap;
  }

  span {
    @include flexAlignCenter;
    svg {
      @include boxSizing(15);
    }

    margin-left: 10px;
    transition: all 0.3s ease-in;
    transform: rotate(180deg);

    &.active {
      transform: rotate(0);
    }
  }

  &.disabledFlow {
    color: #8d8c8c;
  }

  &::after,
  &::before {
    content: '';
    display: block;
    width: 40px;
    height: 1px;
    background: $white;
    margin: 0 10px 0 0;
  }
  &::after {
    width: 100%;
    margin: 0 0 0 10px;
  }
  .light & {
    p {
      color: $gray-6;
    }
    &::after,
    &::before {
      background: $gray-5;
    }

    &.disabledFlow {
      color: #8d8c8c;
    }
  }
}

.collapsePanel {
  animation: growDown 400ms ease-in-out forwards;
  transform-origin: top center;
  padding-left: 30px;
  &:nth-of-type(2) {
    padding-left: 30px;
  }
}

.add-condition-btn {
  @include transparentButton;
  @include tipography(16);
  color: $blue;
  margin-bottom: 15px;

  &.add-condition {
    margin-bottom: 0;
  }

  .plus-sign {
    @include flexMiddle;
    @include boxSizing(20);

    color: $white;
    margin-right: 12px;
    background: $blue;
    font-size: 25px;
    border-radius: 50%;
  }
}

.next-question {
  margin-bottom: 20px;
  label {
    @include tipography(16);
  }
  .light & {
    label {
      color: $gray-6;
    }
  }
}

.select {
  background: linear-gradient(180deg, #3f3f40 0%, #212122 48.96%, #3f3f40 98.96%);
  border: 1px solid $gray-9;
  border-radius: 3px;
  padding: 2px 5px;
  @include tipography(16);
  margin-right: 15px;
  option {
    color: $gray-2;
  }
  &:focus {
    outline: none;
    border: 1px solid $white;
  }
  &.ans {
    min-width: 60px;
  }
  &.and-or {
    margin-left: 60px;
  }
  &.next {
    margin-left: 10px;
  }
  .light & {
    background: linear-gradient(180deg, #ffffff 0%, #edebeb 48.96%, #ffffff 98.96%);
    color: $gray-6;
    &:focus {
      outline: none;
      border: 1px solid $gray-8;
    }
  }
}

.no-condition {
  border-top: 1px solid $white;
  padding: 20px 0;
  @include tipography(16);
  .light & {
    color: $gray-6;
    border-top: 1px solid $gray-5;
  }
}

.condition-wrapper {
  border-top: 1px solid $white;
  padding: 20px 0;
  .light & {
    border-top: 1px solid $gray-5;
  }
}

.condition {
  display: flex;
  align-items: center;
  padding: 0 0 10px;
  flex-wrap: wrap;
  @include tipography(16);

  .delete-condition {
    svg {
      @include boxSizing(15);
    }

    @include transparentButton;
  }

  p {
    margin-right: 10px;
  }

  .light & {
    color: $gray-6;
  }
}

.to {
  display: flex;
  align-items: center;
}
</style>
